import React, { createContext, type PropsWithChildren, useCallback, useState } from 'react';

import type {
	ClearReferrerCallback,
	RefreshSearchSessionIdCallback,
} from '../../../../common/constants/bootstrap';
import type { ProductKeys } from '../../../../common/constants/products';
import type { SearchStoreExperience } from '../../../../common/constants/search';
import { parseQueryParams } from '../../../../common/utils/query-params';
import { type BootstrapContainerProps } from '../index';

import {
	defaultDynamicContext,
	type DynamicBootstrapContextState,
	DynamicContextProvider,
	type DynamicContextProviderProps,
} from './dynamic-context-provider';
import { createStaticBootstrapStore, type StaticBootstrapStore } from './static-bootstrap-store';
import {
	defaultStaticContext,
	type StaticBootstrapContextState,
	StaticContextProvider,
	type StaticContextProviderProps,
} from './static-context-provider';

/**
 * A replacement for RSS store/bootstrap AKA BootstrapStoreContainer
 * Builds StaticBootstrapContext and DynamicBootstrapContext that provide store-like functionality
 *
 * Acts as the top level shared state for values to eventually be prop-drilled (temporary: dynamic context provider)
 *
 * Any `useState` here is an opportunity for that state to be pushed down deeper into the component tree where it is used
 */
export type BootstrapContextProviderProps = BootstrapContainerProps & {
	experience?: SearchStoreExperience;
	accessibleProducts?: ProductKeys[];
};

export const BootstrapContextProvider = ({
	children,
	experience,
	accessibleProducts,
	...props
}: PropsWithChildren<BootstrapContextProviderProps>): React.JSX.Element => {
	const {
		thirdPartyConfigs: initialThirdPartyConfigsProp,
		searchSessionId: searchSessionIdProp,
		smartLinkUsage: smartLinkUsageProp,
	} = props.initialProps || {};
	const safeQueryParams = props.queryParams && parseQueryParams(props.queryParams);

	/**
	 * searchSessionId is generated on the client and does not change frequently, but can be refreshed with refreshSearchSessionId
	 *
	 * This is effectively a global so can't be drilled much deeper than search-page-relay
	 */
	const [searchSessionId, setSearchSessionId] = useState<string>(
		searchSessionIdProp ?? crypto.randomUUID(),
	);
	const refreshSearchSessionId: RefreshSearchSessionIdCallback = useCallback(() => {
		const newSearchSessionId = crypto.randomUUID();
		setSearchSessionId(newSearchSessionId);
	}, []);

	/**
	 * Initial ?referrer=X paramater for analytics
	 * Referrer is actually static for a single page load once, but is cleaned up after use so it isn't re-used in analytics events
	 *
	 * This is effectively a global so can't be drilled much deeper than search-page-relay
	 */
	const [referrer, setReferrer] = useState<string | undefined>(safeQueryParams?.referrer);
	const clearReferrer: ClearReferrerCallback = useCallback(() => {
		setReferrer(undefined);
	}, []);

	/**
	 * Initial ?text=X parameter for search store
	 *
	 * I think this is only used in search-page when checking referrer (not relay) and can be cleaned up with `search-page-relay`
	 */
	const [initialText] = useState<string | undefined>(safeQueryParams?.text);

	const staticProps: StaticContextProviderProps = {
		refreshSearchSessionId,
		clearReferrer,
		initialText,
		initialThirdPartyConfigs: initialThirdPartyConfigsProp || {},
		experience,
		accessibleProducts,
	};
	const dynamicProps: DynamicContextProviderProps = {
		searchSessionId,
		referrer,
		smartLinkUsage: smartLinkUsageProp,
	};

	return (
		<StaticContextProvider {...props} {...staticProps}>
			<DynamicContextProvider {...props} {...dynamicProps}>
				{children}
			</DynamicContextProvider>
		</StaticContextProvider>
	);
};

/**
 * A context provider for static values from the bootstrap phase
 * Guaranteed to never re-render except:
 * - initial render (will use initialState values)
 * - once when all bootstrap queries/logic has finished (final values)
 *
 * Caution: do not place attributes in here that require mutation after bootstrap phase!
 */
export const StaticBootstrapContext: React.Context<StaticBootstrapContextState> =
	createContext<StaticBootstrapContextState>(defaultStaticContext);

/**
 * A context that carries a StaticBootstrapStore instance for granular subscriptions.
 * Consumers use useStaticBootstrapSelector() with this context
 * instead of useContext(StaticBootstrapContext) to avoid broadcast re-renders.
 */
export const StaticBootstrapStoreContext: React.Context<
	StaticBootstrapStore<StaticBootstrapContextState>
> = createContext<StaticBootstrapStore<StaticBootstrapContextState>>(
	createStaticBootstrapStore<StaticBootstrapContextState>(
		defaultStaticContext as StaticBootstrapContextState,
	),
);

/**
 * A context provider for dynamic values from the bootstrap phase
 *
 * Subscribe with caution and make use of useMemo/useCallback to avoid re-renders
 */
export const DynamicBootstrapContext: React.Context<DynamicBootstrapContextState> =
	createContext<DynamicBootstrapContextState>(defaultDynamicContext);
